import { Link } from "react-router-dom";

interface ReportCard {
  title: string;
  description: string;
  icon: JSX.Element;
  path: string;
  color: string;
  bgColor: string;
}

export default function ReportsDashboard() {
  const reports: ReportCard[] = [
    {
      title: "Sales Report",
      description:
        "Analyze sales performance, revenue trends, and payment methods",
      path: "/reports/sales",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      title: "Stock Report",
      description:
        "Monitor inventory levels, stock status, and category-wise breakdown",
      path: "/reports/stock",
      color: "text-green-600",
      bgColor: "bg-green-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      ),
    },
    {
      title: "Expiry Report",
      description: "Track expiring and expired batches for FEFO compliance",
      path: "/reports/expiry",
      color: "text-orange-600",
      bgColor: "bg-orange-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      title: "GST Report",
      description:
        "Generate GSTR-1 returns with HSN-wise summary and tax breakdown",
      path: "/reports/gst",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"
          />
        </svg>
      ),
    },
    {
      title: "Day Book",
      description: "Daily inflow/outflow summary across sales and payments",
      path: "/reports/day-book",
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7h8M8 11h8m-6 4h6M6 3h8l4 4v14H6V3z"
          />
        </svg>
      ),
    },
    {
      title: "Cash Book",
      description: "Track cash-only receipts and payments",
      path: "/reports/cash-book",
      color: "text-amber-600",
      bgColor: "bg-amber-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.1 0-2 .9-2 2m2-2c1.1 0 2 .9 2 2m-2-2v8m0 0c-1.1 0-2-.9-2-2m2 2c1.1 0 2-.9 2-2m4-6V6a2 2 0 00-2-2H6a2 2 0 00-2 2v6m16 0v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6m16 0H4"
          />
        </svg>
      ),
    },
    {
      title: "Counter-wise Report",
      description: "Session totals by counter and billing performance",
      path: "/reports/counter",
      color: "text-sky-600",
      bgColor: "bg-sky-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 7h18M3 12h18M3 17h18"
          />
        </svg>
      ),
    },
    {
      title: "End-of-day Reconciliation",
      description: "Cash reconciliation by session with variance tracking",
      path: "/reports/end-of-day",
      color: "text-red-600",
      bgColor: "bg-red-100",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m2 9H7a2 2 0 01-2-2V5a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
  ];

  const quickStats = [
    {
      label: "Today's Sales",
      value: "₹0.00",
      change: "+0%",
      changeType: "increase",
    },
    {
      label: "Low Stock Items",
      value: "0",
      change: "Monitor",
      changeType: "warning",
    },
    {
      label: "Expiring Soon",
      value: "0",
      change: "30 days",
      changeType: "warning",
    },
    {
      label: "Monthly GST",
      value: "₹0.00",
      change: "Current month",
      changeType: "neutral",
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Reports & Analytics
        </h1>
        <p className="text-gray-600 mt-2">
          Comprehensive business intelligence and compliance reporting
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {quickStats.map((stat, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {stat.value}
                </p>
                <p
                  className={`text-xs mt-1 ${
                    stat.changeType === "increase"
                      ? "text-green-600"
                      : stat.changeType === "warning"
                        ? "text-orange-600"
                        : "text-gray-600"
                  }`}
                >
                  {stat.change}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reports.map((report, index) => (
          <Link
            key={index}
            to={report.path}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border-2 border-transparent hover:border-blue-500"
          >
            <div className="flex items-start space-x-4">
              <div className={`p-3 rounded-lg ${report.bgColor}`}>
                <div className={report.color}>{report.icon}</div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {report.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {report.description}
                </p>
                <div className="flex items-center text-blue-600 text-sm font-medium">
                  <span>View Report</span>
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Additional Information */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <svg
            className="w-6 h-6 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              Excel Export Available
            </h4>
            <p className="text-sm text-blue-800">
              All reports support Excel export for offline analysis and
              record-keeping. Click on any report above to view detailed
              analytics and download formatted spreadsheets.
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <svg
              className="w-6 h-6 text-green-600 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h4 className="font-semibold text-gray-900">Real-time Data</h4>
          </div>
          <p className="text-sm text-gray-600">
            All reports display live data from your current inventory and sales
            records.
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <svg
              className="w-6 h-6 text-blue-600 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <h4 className="font-semibold text-gray-900">Advanced Filters</h4>
          </div>
          <p className="text-sm text-gray-600">
            Filter by date range, store, category, and more to get specific
            insights.
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <svg
              className="w-6 h-6 text-purple-600 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h4 className="font-semibold text-gray-900">GST Compliance</h4>
          </div>
          <p className="text-sm text-gray-600">
            Generate GSTR-1 ready reports with HSN codes and tax breakdowns.
          </p>
        </div>
      </div>
    </div>
  );
}
